import { BASE_URL } from '../components/AppConfig';
import { getBearerToken } from '../utils/authToken';

export const DUMMY_LIVE_OFF_MESSAGE =
  'Dummy live is turned off. It stays off in production, and in development or test when DUMMY_LIVE_MATCH_ENABLED is 0.';

export const DUMMY_LIVE_EVENTS = [
  { event: 'dot', label: 'Dot' },
  { event: 'single', label: '1' },
  { event: 'two', label: '2' },
  { event: 'three', label: '3' },
  { event: 'four', label: 'Four' },
  { event: 'six', label: 'Six' },
  { event: 'wicket', label: 'Wicket' },
  { event: 'fifty', label: 'Fifty' },
  { event: 'hundred', label: 'Hundred' },
  { event: 'start', label: 'Start' },
  { event: 'innings_end', label: 'Innings end' },
  { event: 'match_complete', label: 'Match complete' },
];

const EVENT_NAMES = new Set(['ball', ...DUMMY_LIVE_EVENTS.map((item) => item.event)]);

function defaultMessage(status) {
  if (status === 404) return DUMMY_LIVE_OFF_MESSAGE;
  if (status === 400) return 'That request was rejected.';
  return 'Request failed.';
}

export function isDummyLiveOff(error) {
  return error?.status === 404;
}

export function describeDummyLiveError(error) {
  if (isDummyLiveOff(error)) return DUMMY_LIVE_OFF_MESSAGE;
  const message = String(error?.message || '').trim();
  return message || defaultMessage(error?.status);
}

export function buildDummyLiveAutoplayBody(enabled) {
  if (typeof enabled !== 'boolean') {
    throw new Error('enabled must be true or false');
  }
  return { enabled };
}

export function buildDummyLiveEventBody(event, runs) {
  const name = String(event || '').trim();
  if (!EVENT_NAMES.has(name)) {
    throw new Error('Choose a dummy live event.');
  }
  if (name !== 'ball') return { event: name };
  const raw = runs == null || runs === '' ? 1 : Number(runs);
  if (!Number.isInteger(raw) || raw < 0 || raw > 6) {
    throw new Error('Runs must be a whole number from 0 to 6.');
  }
  return { event: 'ball', runs: raw };
}

async function request(path, { method = 'GET', body } = {}) {
  const token = getBearerToken();
  if (!token) {
    const error = new Error('Not authenticated');
    error.status = 401;
    throw error;
  }

  let response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
      body: body == null ? undefined : JSON.stringify(body),
    });
  } catch {
    const error = new Error('Request failed.');
    error.status = 0;
    throw error;
  }

  const json = await response.json().catch(() => ({}));
  const statusCode = String(json.status_code ?? json.statusCode ?? '');
  if (!response.ok || statusCode !== '10000') {
    const error = new Error(json.message || defaultMessage(response.status));
    error.status = response.status;
    error.statusCode = statusCode;
    throw error;
  }
  return json.data ?? {};
}

export function fetchDummyLive() {
  return request('/admin/dummy-live');
}

export function seedDummyLive() {
  return request('/admin/dummy-live/seed', { method: 'POST', body: {} });
}

export function resetDummyLive(matchId) {
  return request(`/admin/dummy-live/${encodeURIComponent(matchId)}/reset`, {
    method: 'POST',
    body: {},
  });
}

export function setDummyLiveAutoplay(matchId, enabled) {
  return request(`/admin/dummy-live/${encodeURIComponent(matchId)}/autoplay`, {
    method: 'POST',
    body: buildDummyLiveAutoplayBody(enabled),
  });
}

export function sendDummyLiveEvent(matchId, event, runs) {
  return request(`/admin/dummy-live/${encodeURIComponent(matchId)}/event`, {
    method: 'POST',
    body: buildDummyLiveEventBody(event, runs),
  });
}
